import {
  Assignee,
  AssigneeServerResponse,
  StatusReason,
  StatusReasonServerResponse,
  CommentTypes,
  PaymentCompatabilityCheckAttribute,
  SupplierValidationRecordInstructionType,
  SupplierValidationRecordStatus,
  SvManagementFollowUpEmailSenderStatus,
  SvManagementFollowUpEmailSenderStatusDetails,
  SVMasterFileAutomaticEmailStatus,
  SVMasterFileAutomaticEmailStopReason,
  transformAssignee,
} from '@mortee/domain/validationSystem';
import Log from '@app/libs/logger';
import { isDefined } from '@app/utils/utils';
import { AlertStatus } from '@mortee/domain/validationSystemAlerts';

export enum SupplierValidationTimelineEventType {
  svLinked = 'SvLinked',
  svUnlinked = 'SvUnlinked', //no data
  svLinkUpdate = 'SvLinkUpdate',
  statusUpdate = 'StatusUpdate',
  assigneeUpdate = 'AssigneeUpdate',
  isCustomerInvolvementUpdate = 'IsCustomerInvolvementUpdate',
  autoMailerEmailCountUpdate = 'AutoMailerEmailCountUpdate',
  autoMailerStatusUpdate = 'AutoMailerStatusUpdate',
  autoMailerStopEmailUpdate = 'AutoMailerStopEmailUpdate',
  followUpEmailSenderEmailCountUpdate = 'FollowUpEmailSenderEmailCountUpdate',
  followUpEmailSenderStatusUpdate = 'FollowUpEmailSenderStatusUpdate',
  followUpEmailSenderStopEmailsUpdate = 'FollowUpEmailSenderStopEmailsUpdate',
  create = 'Create', //no data
  comment = 'Comment',
  successfulDistribution = 'SuccessfulDistribution',
  compatibilityFailedDistribution = 'CompatibilityFailedDistribution',
  cancelReasonUpdate = 'CancelReasonUpdate',
  clarificationReasonsUpdate = 'ClarificationReasonsUpdate',
  manualInstructionTypeUpdate = 'ManualInstructionTypeUpdate',
  evidenceLink = 'EvidenceLink',
  evidenceDelete = 'EvidenceDelete',
  alertOpen = 'AlertOpen',
  alertResolve = 'AlertResolve',
  manualPayeeNameUpdate = 'ManualPayeeNameUpdate',
  manualPayeeAdditionalNameUpdate = 'ManualPayeeAdditionalNameUpdate',
  manualPayeeAddressUpdate = 'ManualPayeeAddressUpdate',
  manualPayeeAdditionalAddressUpdate = 'ManualPayeeAdditionalAddressUpdate',
  manualPayeeCountryCodeUpdate = 'ManualPayeeCountryCodeUpdate',
  manualPayeeIsPrivateUpdate = 'ManualPayeeIsPrivateUpdate',
  manualPayeeLegalEntityIdentifiersUpdate = 'ManualPayeeLegalEntityIdentifiersUpdate',
  manualAccountCountryCodeUpdate = 'ManualAccountCountryCodeUpdate',
  manualAccountSwiftUpdate = 'ManualAccountSwiftUpdate',
  manualAccountBankCodeUpdate = 'ManualAccountBankCodeUpdate',
  manualAccountBranchCodeUpdate = 'ManualAccountBranchCodeUpdate',
  manualAccountAccountNumberUpdate = 'ManualAccountAccountNumberUpdate',
  manualAccountIbanUpdate = 'ManualAccountIbanUpdate',
  manualAccountLocalFormatUpdate = 'ManualAccountLocalFormatUpdate',
  manualAccountBankNameUpdate = 'ManualAccountBankNameUpdate',
  manualAccountFurtherCreditUpdate = 'ManualAccountFurtherCreditUpdate',
  manualAccountTypeUpdate = 'ManualAccountTypeUpdate',
  manualAccountIsPrivateUpdate = 'ManualAccountIsPrivateUpdate',
  finalPayeeNameUpdate = 'FinalPayeeNameUpdate',
  finalPayeeAdditionalNameUpdate = 'FinalPayeeAdditionalNameUpdate',
  finalPayeeAddressUpdate = 'FinalPayeeAddressUpdate',
  finalPayeeAdditionalAddressUpdate = 'FinalPayeeAdditionalAddressUpdate',
  finalPayeeCountryCodeUpdate = 'FinalPayeeCountryCodeUpdate',
  finalPayeeIsPrivateUpdate = 'FinalPayeeIsPrivateUpdate',
  finalPayeeLegalEntityIdentifiersUpdate = 'FinalPayeeLegalEntityIdentifiersUpdate',
  finalAccountCountryCodeUpdate = 'FinalAccountCountryCodeUpdate',
  finalAccountSwiftUpdate = 'FinalAccountSwiftUpdate',
  finalAccountBankCodeUpdate = 'FinalAccountBankCodeUpdate',
  finalAccountBranchCodeUpdate = 'FinalAccountBranchCodeUpdate',
  finalAccountAccountNumberUpdate = 'FinalAccountAccountNumberUpdate',
  finalAccountIbanUpdate = 'FinalAccountIbanUpdate',
  finalAccountLocalFormatUpdate = 'FinalAccountLocalFormatUpdate',
  finalAccountBankNameUpdate = 'FinalAccountBankNameUpdate',
  finalAccountFurtherCreditUpdate = 'FinalAccountFurtherCreditUpdate',
  finalAccountTypeUpdate = 'FinalAccountTypeUpdate',
  finalAccountIsPrivateUpdate = 'FinalAccountIsPrivateUpdate',
}

export enum DistributionType {
  onlyMarkedAsDistributed = 'OnlyMarkedAsDistributed',
  distributed = 'Distributed',
}

export enum DistributionTrigger {
  onLink = 'OnLink',
  scheduled = 'Scheduled',
  userRequest = 'UserRequest',
  manualInstructionTypeUpdate = 'ManualInstructionTypeUpdate',
}

export interface ActivityLogServerResponse {
  events: ActivityLogEventServerResponse[];
}

interface ActivityLogBaseEventServerResponse<TType extends SupplierValidationTimelineEventType, TData> {
  type: TType;
  metadata: BaseActivityLogEventMetaDataServerResponse;
  data: TData;
}

export type ActivityLogEventServerResponse =
  | SvLinkedEventServerResponse
  | SvUnlinkedEventServerResponse
  | SvLinkUpdateEventServerResponse
  | StatusUpdateEventServerResponse
  | AssigneeUpdateEventServerResponse
  | IsCustomerInvolvementUpdateEventServerResponse
  | AutoMailerEmailCountUpdateEventServerResponse
  | AutoMailerStatusUpdateEventServerResponse
  | AutoMailerStopEmailUpdateEventServerResponse
  | FollowUpEmailSenderEmailCountUpdateEventServerResponse
  | FollowUpEmailSenderStatusUpdateEventServerResponse
  | FollowUpEmailSenderStopEmailsUpdateEventServerResponse
  | CreateEventServerResponse
  | CommentEventServerResponse
  | SuccessfulDistributionEventServerResponse
  | CompatibilityFailedDistributionEventServerResponse
  | CancelReasonUpdateUpdateServerResponse
  | ClarificationReasonsUpdateServerResponse
  | ManualInstructionTypeUpdateServerResponse
  | EvidenceLinkEventServerResponse
  | EvidenceDeleteEventServerResponse
  | AlertOpenEventServerResponse
  | AlertResolveEventServerResponse
  | ManualPayeeNameUpdateEventServerResponse
  | ManualPayeeAdditionalNameUpdateEventServerResponse
  | ManualPayeeAddressUpdateEventServerResponse
  | ManualPayeeAdditionalAddressUpdateEventServerResponse
  | ManualPayeeCountryCodeUpdateEventServerResponse
  | ManualPayeeIsPrivateUpdateEventServerResponse
  | ManualPayeeLegalEntityIdentifiersUpdateEventServerResponse
  | ManualAccountCountryCodeUpdateEventServerResponse
  | ManualAccountSwiftUpdateEventServerResponse
  | ManualAccountBankCodeUpdateEventServerResponse
  | ManualAccountBranchCodeUpdateEventServerResponse
  | ManualAccountAccountNumberUpdateEventServerResponse
  | ManualAccountIbanUpdateEventServerResponse
  | ManualAccountLocalFormatUpdateEventServerResponse
  | ManualAccountBankNameUpdateEventServerResponse
  | ManualAccountFurtherCreditUpdateEventServerResponse
  | ManualAccountTypeUpdateEventServerResponse
  | ManualAccountIsPrivateUpdateEventServerResponse
  | FinalPayeeNameUpdateEventServerResponse
  | FinalPayeeAdditionalNameUpdateEventServerResponse
  | FinalPayeeAddressUpdateEventServerResponse
  | FinalPayeeAdditionalAddressUpdateEventServerResponse
  | FinalPayeeCountryCodeUpdateEventServerResponse
  | FinalPayeeIsPrivateUpdateEventServerResponse
  | FinalPayeeLegalEntityIdentifiersUpdateEventServerResponse
  | FinalAccountCountryCodeUpdateEventServerResponse
  | FinalAccountSwiftUpdateEventServerResponse
  | FinalAccountBankCodeUpdateEventServerResponse
  | FinalAccountBranchCodeUpdateEventServerResponse
  | FinalAccountAccountNumberUpdateEventServerResponse
  | FinalAccountIbanUpdateEventServerResponse
  | FinalAccountLocalFormatUpdateEventServerResponse
  | FinalAccountBankNameUpdateEventServerResponse
  | FinalAccountFurtherCreditUpdateEventServerResponse
  | FinalAccountTypeUpdateEventServerResponse
  | FinalAccountIsPrivateUpdateEventServerResponse;

export interface BaseActivityLogEventMetaDataServerResponse {
  performingUserId: string;
  performingUserName: string;
  timestamp: number;
}

export type SvLinkedEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.svLinked,
  {
    svRegistrationId: string;
    svRegistrationNumber: string;
    registrationTimestamp: number;
    supplierName: string;
  }
>;

export type SvUnlinkedEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.svUnlinked,
  null
>;

export type SvLinkUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.svLinkUpdate,
  {
    svRegistrationId: string;
    svRegistrationNumber: string;
    registrationTimestamp: string;
    supplierName: string;
  }
>;

export type StatusUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.statusUpdate,
  {
    status: SupplierValidationRecordStatus;
  }
>;

export type AssigneeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.assigneeUpdate,
  {
    assignee: AssigneeServerResponse | null | undefined;
  }
>;

export type IsCustomerInvolvementUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.isCustomerInvolvementUpdate,
  {
    isCustomerInvolvementRequired: boolean;
  }
>;

export type AutoMailerEmailCountUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.autoMailerEmailCountUpdate,
  {
    emailCount: number;
  }
>;

export type AutoMailerStatusUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.autoMailerStatusUpdate,
  {
    emailStatus: SVMasterFileAutomaticEmailStatus;
    emailMessage: SVMasterFileAutomaticEmailStopReason | null | undefined;
  }
>;

export type AutoMailerStopEmailUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.autoMailerStopEmailUpdate,
  {
    stopEmails: boolean;
  }
>;

export type FollowUpEmailSenderEmailCountUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.followUpEmailSenderEmailCountUpdate,
  {
    count: number;
  }
>;

export type FollowUpEmailSenderStatusUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.followUpEmailSenderStatusUpdate,
  {
    status: SvManagementFollowUpEmailSenderStatus;
    details: SvManagementFollowUpEmailSenderStatusDetails | null | undefined;
  }
>;

export type FollowUpEmailSenderStopEmailsUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.followUpEmailSenderStopEmailsUpdate,
  {
    stopEmails: boolean;
  }
>;

export type CreateEventServerResponse = ActivityLogBaseEventServerResponse<SupplierValidationTimelineEventType.create, null>;

export type EvidenceLinkEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.evidenceLink,
  null
>;

export type EvidenceDeleteEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.evidenceDelete,
  null
>;

export type CommentEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.comment,
  {
    id: string;
    comment: string;
    commentType: CommentTypes;
  }
>;

export type SuccessfulDistributionEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.successfulDistribution,
  {
    distributionType: DistributionType;
    distributionTrigger: DistributionTrigger;
  }
>;

export type CompatibilityFailedDistributionEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.compatibilityFailedDistribution,
  {
    distributionType: DistributionType;
    distributionTrigger: DistributionTrigger;
    failureReasons: PaymentCompatabilityCheckAttribute[];
  }
>;

export type CancelReasonUpdateUpdateServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.cancelReasonUpdate,
  StatusReasonServerResponse | null | undefined
>;

export type ClarificationReasonsUpdateServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.clarificationReasonsUpdate,
  StatusReasonServerResponse[] | null | undefined
>;

export type ManualInstructionTypeUpdateServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualInstructionTypeUpdate,
  { manualInstructionType: SupplierValidationRecordInstructionType | null | undefined }
>;

export type AlertOpenEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.alertOpen,
  {
    alertId: string;
    alertText: string;
    status: AlertStatus;
  }
>;

export type AlertResolveEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.alertResolve,
  {
    alertId: string;
    alertText: string;
    status: AlertStatus;
  }
>;

export type ManualPayeeNameUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualPayeeNameUpdate,
  null
>;
export type ManualPayeeAdditionalNameUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualPayeeAdditionalNameUpdate,
  null
>;
export type ManualPayeeAddressUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualPayeeAddressUpdate,
  null
>;
export type ManualPayeeAdditionalAddressUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualPayeeAdditionalAddressUpdate,
  null
>;
export type ManualPayeeCountryCodeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualPayeeCountryCodeUpdate,
  null
>;
export type ManualPayeeIsPrivateUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualPayeeIsPrivateUpdate,
  null
>;
export type ManualPayeeLegalEntityIdentifiersUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualPayeeLegalEntityIdentifiersUpdate,
  null
>;
export type ManualAccountCountryCodeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountCountryCodeUpdate,
  null
>;
export type ManualAccountSwiftUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountSwiftUpdate,
  null
>;
export type ManualAccountBankCodeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountBankCodeUpdate,
  null
>;
export type ManualAccountBranchCodeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountBranchCodeUpdate,
  null
>;
export type ManualAccountAccountNumberUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountAccountNumberUpdate,
  null
>;
export type ManualAccountIbanUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountIbanUpdate,
  null
>;
export type ManualAccountLocalFormatUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountLocalFormatUpdate,
  null
>;
export type ManualAccountBankNameUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountBankNameUpdate,
  null
>;
export type ManualAccountFurtherCreditUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountFurtherCreditUpdate,
  null
>;
export type ManualAccountTypeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountTypeUpdate,
  null
>;
export type ManualAccountIsPrivateUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.manualAccountIsPrivateUpdate,
  null
>;
export type FinalPayeeNameUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalPayeeNameUpdate,
  null
>;
export type FinalPayeeAdditionalNameUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalPayeeAdditionalNameUpdate,
  null
>;
export type FinalPayeeAddressUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalPayeeAddressUpdate,
  null
>;
export type FinalPayeeAdditionalAddressUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalPayeeAdditionalAddressUpdate,
  null
>;
export type FinalPayeeCountryCodeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalPayeeCountryCodeUpdate,
  null
>;
export type FinalPayeeIsPrivateUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalPayeeIsPrivateUpdate,
  null
>;
export type FinalPayeeLegalEntityIdentifiersUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalPayeeLegalEntityIdentifiersUpdate,
  null
>;
export type FinalAccountCountryCodeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountCountryCodeUpdate,
  null
>;
export type FinalAccountSwiftUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountSwiftUpdate,
  null
>;
export type FinalAccountBankCodeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountBankCodeUpdate,
  null
>;
export type FinalAccountBranchCodeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountBranchCodeUpdate,
  null
>;
export type FinalAccountAccountNumberUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountAccountNumberUpdate,
  null
>;
export type FinalAccountIbanUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountIbanUpdate,
  null
>;
export type FinalAccountLocalFormatUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountLocalFormatUpdate,
  null
>;
export type FinalAccountBankNameUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountBankNameUpdate,
  null
>;
export type FinalAccountFurtherCreditUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountFurtherCreditUpdate,
  null
>;
export type FinalAccountTypeUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountTypeUpdate,
  null
>;
export type FinalAccountIsPrivateUpdateEventServerResponse = ActivityLogBaseEventServerResponse<
  SupplierValidationTimelineEventType.finalAccountIsPrivateUpdate,
  null
>;

export interface ActivityLog {
  events: ActivityLogEvent[];
}

interface ActivityLogBaseEvent<TType extends SupplierValidationTimelineEventType, TData> {
  type: TType;
  metadata: BaseActivityLogEventMetaData;
  data: TData;
}

export type ActivityLogEvent =
  | SvLinkedEvent
  | SvUnlinkedEvent
  | SvLinkUpdateEvent
  | StatusUpdateEvent
  | AssigneeUpdateEvent
  | IsCustomerInvolvementUpdateEvent
  | AutoMailerEmailCountUpdateEvent
  | AutoMailerStatusUpdateEvent
  | AutoMailerStopEmailUpdateEvent
  | FollowUpEmailSenderEmailCountUpdateEvent
  | FollowUpEmailSenderStatusUpdateEvent
  | FollowUpEmailSenderStopEmailsUpdateEvent
  | CreateEvent
  | CommentEvent
  | SuccessfulDistributionEvent
  | CompatibilityFailedDistributionEvent
  | CancelReasonUpdateUpdateEvent
  | ClarificationReasonsUpdateEvent
  | ManualInstructionTypeUpdateEvent
  | EvidenceLinkEvent
  | EvidenceDeleteEvent
  | AlertOpenEvent
  | AlertResolveEvent
  | ManualPayeeNameUpdateEvent
  | ManualPayeeAdditionalNameUpdateEvent
  | ManualPayeeAddressUpdateEvent
  | ManualPayeeAdditionalAddressUpdateEvent
  | ManualPayeeCountryCodeUpdateEvent
  | ManualPayeeIsPrivateUpdateEvent
  | ManualPayeeLegalEntityIdentifiersUpdateEvent
  | ManualAccountCountryCodeUpdateEvent
  | ManualAccountSwiftUpdateEvent
  | ManualAccountBankCodeUpdateEvent
  | ManualAccountBranchCodeUpdateEvent
  | ManualAccountAccountNumberUpdateEvent
  | ManualAccountIbanUpdateEvent
  | ManualAccountLocalFormatUpdateEvent
  | ManualAccountBankNameUpdateEvent
  | ManualAccountFurtherCreditUpdateEvent
  | ManualAccountTypeUpdateEvent
  | ManualAccountIsPrivateUpdateEvent
  | FinalPayeeNameUpdateEvent
  | FinalPayeeAdditionalNameUpdateEvent
  | FinalPayeeAddressUpdateEvent
  | FinalPayeeAdditionalAddressUpdateEvent
  | FinalPayeeCountryCodeUpdateEvent
  | FinalPayeeIsPrivateUpdateEvent
  | FinalPayeeLegalEntityIdentifiersUpdateEvent
  | FinalAccountCountryCodeUpdateEvent
  | FinalAccountSwiftUpdateEvent
  | FinalAccountBankCodeUpdateEvent
  | FinalAccountBranchCodeUpdateEvent
  | FinalAccountAccountNumberUpdateEvent
  | FinalAccountIbanUpdateEvent
  | FinalAccountLocalFormatUpdateEvent
  | FinalAccountBankNameUpdateEvent
  | FinalAccountFurtherCreditUpdateEvent
  | FinalAccountTypeUpdateEvent
  | FinalAccountIsPrivateUpdateEvent;

export interface BaseActivityLogEventMetaData {
  performingUserId: string;
  performingUserName: string;
  timestamp: number;
}

export type SvLinkedEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.svLinked,
  {
    svRegistrationId: string;
    svRegistrationNumber: string;
    registrationTimestamp: number;
    supplierName: string;
  }
>;

export type SvUnlinkedEvent = ActivityLogBaseEvent<SupplierValidationTimelineEventType.svUnlinked, null>;

export type SvLinkUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.svLinkUpdate,
  {
    svRegistrationId: string;
    svRegistrationNumber: string;
    registrationTimestamp: string;
    supplierName: string;
  }
>;

export type StatusUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.statusUpdate,
  {
    status: SupplierValidationRecordStatus;
  }
>;

export type AssigneeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.assigneeUpdate,
  {
    assignee: Assignee | null;
  }
>;

export type IsCustomerInvolvementUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.isCustomerInvolvementUpdate,
  {
    isCustomerInvolvementRequired: boolean;
  }
>;

export type AutoMailerEmailCountUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.autoMailerEmailCountUpdate,
  {
    emailCount: number;
  }
>;

export type AutoMailerStatusUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.autoMailerStatusUpdate,
  {
    emailStatus: SVMasterFileAutomaticEmailStatus;
    emailMessage: SVMasterFileAutomaticEmailStopReason | null;
  }
>;

export type AutoMailerStopEmailUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.autoMailerStopEmailUpdate,
  {
    stopEmails: boolean;
  }
>;

export type FollowUpEmailSenderEmailCountUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.followUpEmailSenderEmailCountUpdate,
  {
    count: number;
  }
>;

export type FollowUpEmailSenderStatusUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.followUpEmailSenderStatusUpdate,
  {
    status: SvManagementFollowUpEmailSenderStatus;
    details: SvManagementFollowUpEmailSenderStatusDetails | null;
  }
>;

export type FollowUpEmailSenderStopEmailsUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.followUpEmailSenderStopEmailsUpdate,
  {
    stopEmails: boolean;
  }
>;

export type CancelReasonUpdateUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.cancelReasonUpdate,
  StatusReason | null
>;

export type ClarificationReasonsUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.clarificationReasonsUpdate,
  StatusReason[]
>;

export type ManualInstructionTypeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualInstructionTypeUpdate,
  { manualInstructionType: SupplierValidationRecordInstructionType | null }
>;

export type CreateEvent = ActivityLogBaseEvent<SupplierValidationTimelineEventType.create, null>;

export type EvidenceLinkEvent = ActivityLogBaseEvent<SupplierValidationTimelineEventType.evidenceLink, null>;

export type EvidenceDeleteEvent = ActivityLogBaseEvent<SupplierValidationTimelineEventType.evidenceDelete, null>;

export type ManualPayeeNameUpdateEvent = ActivityLogBaseEvent<SupplierValidationTimelineEventType.manualPayeeNameUpdate, null>;
export type ManualPayeeAdditionalNameUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualPayeeAdditionalNameUpdate,
  null
>;
export type ManualPayeeAddressUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualPayeeAddressUpdate,
  null
>;
export type ManualPayeeAdditionalAddressUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualPayeeAdditionalAddressUpdate,
  null
>;
export type ManualPayeeCountryCodeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualPayeeCountryCodeUpdate,
  null
>;
export type ManualPayeeIsPrivateUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualPayeeIsPrivateUpdate,
  null
>;
export type ManualPayeeLegalEntityIdentifiersUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualPayeeLegalEntityIdentifiersUpdate,
  null
>;
export type ManualAccountCountryCodeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountCountryCodeUpdate,
  null
>;
export type ManualAccountSwiftUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountSwiftUpdate,
  null
>;
export type ManualAccountBankCodeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountBankCodeUpdate,
  null
>;
export type ManualAccountBranchCodeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountBranchCodeUpdate,
  null
>;
export type ManualAccountAccountNumberUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountAccountNumberUpdate,
  null
>;
export type ManualAccountIbanUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountIbanUpdate,
  null
>;
export type ManualAccountLocalFormatUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountLocalFormatUpdate,
  null
>;
export type ManualAccountBankNameUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountBankNameUpdate,
  null
>;
export type ManualAccountFurtherCreditUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountFurtherCreditUpdate,
  null
>;
export type ManualAccountTypeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountTypeUpdate,
  null
>;
export type ManualAccountIsPrivateUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.manualAccountIsPrivateUpdate,
  null
>;
export type FinalPayeeNameUpdateEvent = ActivityLogBaseEvent<SupplierValidationTimelineEventType.finalPayeeNameUpdate, null>;
export type FinalPayeeAdditionalNameUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalPayeeAdditionalNameUpdate,
  null
>;
export type FinalPayeeAddressUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalPayeeAddressUpdate,
  null
>;
export type FinalPayeeAdditionalAddressUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalPayeeAdditionalAddressUpdate,
  null
>;
export type FinalPayeeCountryCodeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalPayeeCountryCodeUpdate,
  null
>;
export type FinalPayeeIsPrivateUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalPayeeIsPrivateUpdate,
  null
>;
export type FinalPayeeLegalEntityIdentifiersUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalPayeeLegalEntityIdentifiersUpdate,
  null
>;
export type FinalAccountCountryCodeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalAccountCountryCodeUpdate,
  null
>;
export type FinalAccountSwiftUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalAccountSwiftUpdate,
  null
>;
export type FinalAccountBankCodeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalAccountBankCodeUpdate,
  null
>;
export type FinalAccountBranchCodeUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalAccountBranchCodeUpdate,
  null
>;
export type FinalAccountAccountNumberUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalAccountAccountNumberUpdate,
  null
>;
export type FinalAccountIbanUpdateEvent = ActivityLogBaseEvent<SupplierValidationTimelineEventType.finalAccountIbanUpdate, null>;
export type FinalAccountLocalFormatUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalAccountLocalFormatUpdate,
  null
>;
export type FinalAccountBankNameUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalAccountBankNameUpdate,
  null
>;
export type FinalAccountFurtherCreditUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalAccountFurtherCreditUpdate,
  null
>;
export type FinalAccountTypeUpdateEvent = ActivityLogBaseEvent<SupplierValidationTimelineEventType.finalAccountTypeUpdate, null>;
export type FinalAccountIsPrivateUpdateEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.finalAccountIsPrivateUpdate,
  null
>;

export type CommentEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.comment,
  {
    comment: {
      id: string;
      content: string;
      commentType: CommentTypes;
    };
  }
>;

export type SuccessfulDistributionEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.successfulDistribution,
  {
    distributionType: DistributionType;
    distributionTrigger: DistributionTrigger;
  }
>;

export type CompatibilityFailedDistributionEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.compatibilityFailedDistribution,
  {
    distributionType: DistributionType;
    distributionTrigger: DistributionTrigger;
    failureReasons: PaymentCompatabilityCheckAttribute[];
  }
>;

export type AlertOpenEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.alertOpen,
  {
    alertId: string;
    alertText: string;
    status: AlertStatus;
  }
>;

export type AlertResolveEvent = ActivityLogBaseEvent<
  SupplierValidationTimelineEventType.alertResolve,
  {
    alertId: string;
    alertText: string;
    status: AlertStatus;
  }
>;

export function transformActivityLog(serverResponse: ActivityLogServerResponse, myUserId: string | undefined): ActivityLog {
  return {
    events: serverResponse.events.map((event) => transformActivityLogEvent(event, myUserId)).filter(isDefined),
  };
}

function transformActivityLogEvent(
  serverResponse: ActivityLogEventServerResponse,
  myUserId: string | undefined,
): ActivityLogEvent | null {
  switch (serverResponse.type) {
    case SupplierValidationTimelineEventType.svLinked:
      return serverResponse;
    case SupplierValidationTimelineEventType.svUnlinked:
      return serverResponse;
    case SupplierValidationTimelineEventType.svLinkUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.statusUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.assigneeUpdate: {
      return {
        ...serverResponse,
        data: {
          assignee: serverResponse.data.assignee ? transformAssignee(serverResponse.data.assignee, myUserId) : null,
        },
      };
    }
    case SupplierValidationTimelineEventType.isCustomerInvolvementUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.autoMailerEmailCountUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.autoMailerStatusUpdate: {
      return {
        ...serverResponse,
        data: {
          ...serverResponse.data,
          emailMessage: serverResponse.data.emailMessage ?? null,
        },
      };
    }
    case SupplierValidationTimelineEventType.autoMailerStopEmailUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.followUpEmailSenderEmailCountUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.followUpEmailSenderStatusUpdate: {
      return {
        ...serverResponse,
        data: {
          ...serverResponse.data,
          details: serverResponse.data.details ?? null,
        },
      };
    }
    case SupplierValidationTimelineEventType.followUpEmailSenderStopEmailsUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.create:
      return serverResponse;
    case SupplierValidationTimelineEventType.comment: {
      return {
        ...serverResponse,
        data: {
          comment: {
            id: serverResponse.data.id,
            content: serverResponse.data.comment,
            commentType: serverResponse.data.commentType,
          },
        },
      };
    }
    case SupplierValidationTimelineEventType.successfulDistribution: {
      return serverResponse;
    }
    case SupplierValidationTimelineEventType.compatibilityFailedDistribution: {
      return serverResponse;
    }
    case SupplierValidationTimelineEventType.cancelReasonUpdate: {
      return {
        ...serverResponse,
        data: serverResponse.data ?? null,
      };
    }
    case SupplierValidationTimelineEventType.clarificationReasonsUpdate: {
      return {
        ...serverResponse,
        data: serverResponse.data ?? [],
      };
    }
    case SupplierValidationTimelineEventType.manualInstructionTypeUpdate: {
      return {
        ...serverResponse,
        data: {
          ...serverResponse.data,
          manualInstructionType: serverResponse.data.manualInstructionType ?? null,
        },
      };
    }
    case SupplierValidationTimelineEventType.evidenceLink:
      return serverResponse;
    case SupplierValidationTimelineEventType.evidenceDelete:
      return serverResponse;
    case SupplierValidationTimelineEventType.alertOpen:
      return serverResponse;
    case SupplierValidationTimelineEventType.alertResolve:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualPayeeNameUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualPayeeAdditionalNameUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualPayeeAddressUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualPayeeAdditionalAddressUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualPayeeCountryCodeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualPayeeIsPrivateUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualPayeeLegalEntityIdentifiersUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountCountryCodeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountSwiftUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountBankCodeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountBranchCodeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountAccountNumberUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountIbanUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountLocalFormatUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountBankNameUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountFurtherCreditUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountTypeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.manualAccountIsPrivateUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalPayeeNameUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalPayeeAdditionalNameUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalPayeeAddressUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalPayeeAdditionalAddressUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalPayeeCountryCodeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalPayeeIsPrivateUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalPayeeLegalEntityIdentifiersUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountCountryCodeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountSwiftUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountBankCodeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountBranchCodeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountAccountNumberUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountIbanUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountLocalFormatUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountBankNameUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountFurtherCreditUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountTypeUpdate:
      return serverResponse;
    case SupplierValidationTimelineEventType.finalAccountIsPrivateUpdate:
      return serverResponse;
    default: {
      Log.exception(new Error(`missing event type: ${serverResponse}`));
      return null;
    }
  }
}

export function isEventAComment(event: ActivityLogEvent): event is CommentEvent {
  return event.type === SupplierValidationTimelineEventType.comment;
}
